import {
  Configuration,
  PersonSessionCreate,
  SessionCreate,
  SessionsApi,
  typedErrorMiddleware,
  WebhookPersonScopeType
} from "@wingspanhq/wingspan-api-sdk";
import { config } from "../config";
import { tokenStore } from "./tokenStore";

export interface LoginCaptchaOptions {
  captchaToken?: string;
  captchaVersion?: string;
  forceCaptcha?: boolean;
}

class SessionService {
  // A dedicated client rather than the shared `apiConfig` from `@api`: login
  // and refresh authenticate off the request body, so `authRefreshMiddleware`
  // would recurse on their 401s, and `mfaChallengeMiddleware` has no bearer to
  // run a challenge with. `typedErrorMiddleware` must stay, though: without it
  // the SDK rejects with its generic `ResponseError` ("Response returned an
  // error code") and the sign-in screen cannot show the API's problem detail.
  private readonly api = new SessionsApi(
    new Configuration({
      basePath: config.apiBaseUrl,
      accessToken: () => tokenStore.getAccessToken() ?? "",
      middleware: [typedErrorMiddleware]
    })
  );

  private refreshInFlight: Promise<void> | null = null;

  async login(
    email: string,
    password: string,
    persist: boolean,
    captcha: LoginCaptchaOptions = {}
  ): Promise<void> {
    // The cast is required, not defensive: SDK 2.x generates `SessionCreate` as
    // an XOR of `{ sessionType: 'Person' } & PersonSessionCreate` (and the Guest
    // / Invite twins). `PersonSessionCreate.sessionType` is the nominal enum
    // `WebhookPersonScopeType`, and intersecting that with the string literal
    // `'Person'` yields `never` — so every variant of the union is
    // uninhabitable and no literal can satisfy it. Build the variant type, then
    // cast once at the SDK boundary.
    // The API enforces reCAPTCHA on production password logins. Outside
    // production `callWithCaptcha` yields empty strings — omit them so the
    // API's "not provided" check sees a clean body.
    const sessionCreate: PersonSessionCreate = {
      sessionType: WebhookPersonScopeType.PERSON,
      email,
      password,
      ...(captcha.captchaToken && {
        captchaToken: captcha.captchaToken,
        captchaVersion: captcha.captchaVersion
      }),
      ...(captcha.forceCaptcha && { forceCaptcha: true })
    };

    const session = await this.api.createSession({
      sessionCreate: sessionCreate as unknown as SessionCreate
    });
    tokenStore.setSession(session, persist);
  }

  // Google sign-in exchanges the Google ID token for a Wingspan session. Unlike
  // `createSession` the request model is a plain interface, so no cast is
  // needed. reCAPTCHA does not apply — Google has already asserted the identity.
  async loginWithGoogle(idToken: string, persist: boolean): Promise<void> {
    const session = await this.api.createGoogleSession({
      googleSessionCreate: { idToken }
    });
    tokenStore.setSession(session, persist);
  }

  async switchAccount(
    accountId: string,
    options?: { preferred?: boolean; reload?: boolean }
  ): Promise<void> {
    // No `sessionId` while impersonating, so this is already unreachable for an
    // impersonated session — as it should be: the token is frozen to one
    // Account and there is nothing to re-scope.
    const identity = tokenStore.getIdentity();
    if (!identity?.sessionId) {
      throw new Error("Cannot switch account without an active session");
    }

    const session = await this.api.updateSession({
      sessionId: identity.sessionId,
      sessionPatch: { accountScope: { accountId } }
    });
    tokenStore.setSession(session);
    if (options?.preferred && identity.personId) {
      tokenStore.setPreferredAccountId(identity.personId, accountId);
    }

    if (options?.reload) {
      window.location.reload();
    }
  }

  async logout(): Promise<void> {
    // Drop the impersonation first so the delete goes out as the admin: an
    // impersonation token cannot address the admin's session, and the admin
    // session is the one that must not survive a logout. The impersonation
    // session needs no call of its own — it is short-lived and expires alone.
    tokenStore.clearImpersonation();

    const identity = tokenStore.getOwnIdentity();
    try {
      if (identity?.sessionId) {
        await this.api.deleteSession({ sessionId: identity.sessionId });
      }
    } finally {
      tokenStore.clear();
    }
  }

  refreshAccessToken(): Promise<void> {
    if (!this.refreshInFlight) {
      const refreshToken = tokenStore.getRefreshToken();
      if (!refreshToken) {
        return Promise.reject(new Error("No refresh token available"));
      }

      this.refreshInFlight = this.api
        .refreshSession({ refreshRequest: { refreshToken } })
        .then(session => tokenStore.setSession(session))
        .finally(() => {
          this.refreshInFlight = null;
        });
    }

    return this.refreshInFlight;
  }

  async restore(): Promise<boolean> {
    if (!tokenStore.getRefreshToken()) {
      return false;
    }

    try {
      await this.refreshAccessToken();
      return true;
    } catch {
      tokenStore.clear();
      return false;
    }
  }
}

export const sessionService = new SessionService();
